/*
 * Copyright (c) 2021 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import * as React from 'react';
import { Component } from "react";
import { inject, observer } from "mobx-react";

import { Button, Modal, Progress } from "@scuf/common";

import './IdentityPackage.scss';
import DIYAppConstants from '../../../utils/DIYAppConstants';
import GatewayIdentityStore from '../../../stores/GatewayManager/GatewayIdentityStore';
import { AxiosResponse } from 'axios';
import featureFlagStore from '../../../utils/FeatureFlags/APFeatureFlagsStore';
import { Gateway } from '../../VirtualGateway/GatewayList/GatewayList';
import GlobalStrings from '../../../utils/GlobalStrings';

/**
 * The properties for the Identity Package component
 */
export interface IIdentityPackageProps
{
    /**
     * The serial number to download package
     */
    serialNumber: string;
    /**
     * Organization Id
     */
    organizationId: string;
    /**
     * Type
     */
    type: String;

    gatewayIdentityStore?: GatewayIdentityStore;
    setGatewayCsr?: any;
    gatewayType?: string;
    gateway?: Gateway;
    setGatewaySerialNumber?: any;
}

/**
 * The state for the Identity Package component
 */
export interface IIdentityPackageState
{
    downloadConfirmation: boolean;
    copyConfirmation: boolean;
    uploadConfirmation: boolean;
    reUploadConfirmation: boolean;
    uploadConfigConfirmation: boolean;
    reUploadConfigConfirmation: boolean;

    ref: React.RefObject<HTMLAnchorElement>;

    identity?: string;
    actionInProgress: boolean;
    isActionFailed?: boolean;
    errorMessage?: string;
    gatewayConfig?:any;
}

/**
 *  The user interface component for Identity Package
 */
@inject(DIYAppConstants.GATEWAY_IDENTITY_STORE)
@observer
export default class IdentityPackage extends Component<IIdentityPackageProps, IIdentityPackageState> 
{
    csrContent: string;

    /**
     * Constructor injection of site selection props
     */
    constructor(props: IIdentityPackageProps)
    {
        super(props);
        this.state = 
        {
            downloadConfirmation: false,
            copyConfirmation: false,
            uploadConfirmation: false,
            reUploadConfirmation: false,
            uploadConfigConfirmation: false,
            reUploadConfigConfirmation: false,

            ref: React.createRef(),

            identity: '',
            isActionFailed: false,
            errorMessage: undefined,
            actionInProgress: false,
        };
        
        this.csrContent = '';
    }

    /**
     *  Render the site selection user interface
     */
    render()
    {
        return (
            <React.Fragment>
                <a style={{ display: 'none' }} href='' ref={this.state.ref}>ref</a>
                {this.renderOnType()}
            </React.Fragment>
        );
    }
    renderOnType = () =>
    {
        let type = this.props.type;

        if (type === 'download')
        {
            return (
                <div id='download-div'>
                    {this.renderDownloadModal()}
                    <Button id="download-button" type="secondary" className="copy-button-font" size="small" minWidth="216px" content="DOWNLOAD IDENTITY"
                        onClick={() =>
                        {
                            this.fetchPackageToDownload();
                            this.setState({ downloadConfirmation: true })
                        }} />
                </div>);
        }
        else if (type === 'copy')
        {
            return (<div>
                {this.renderCopyModal()}
                <Button id="copy-button" type="primary" className="copy-button-font" size="small" minWidth="216px" content="COPY IDENTITY"
                    onClick={() =>
                    {
                        this.fetchAndCopyIdentity();
                        this.setState({ copyConfirmation: true });
                    }} />
            </div>);
        }
        else if (type === 'upload') 
        {
            return (
                <div>
                    {this.renderUploadModal()}
                    <Button id="upload-button" type="primary" className="copy-button-font" size="small" minWidth="216px" content="UPLOAD CSR"
                        onClick={() => 
                        {
                            this.setState({ uploadConfirmation: true });
                        }} />
                </div>);
        }
        else if (type === 're-upload') 
        {
            return (
                <div>
                    {this.renderReUploadModal()}
                    {this.renderUploadModal()}
                    <Button id="re-upload-button" type="primary" className="re-upload-button-font" size="small" minWidth="216px" content="Re-Upload CSR"
                        onClick={() => 
                        {
                            this.setState({ reUploadConfirmation: true });
                        }} />
                </div>);
        }
        else if (type === 'upload-config') 
        {
            return (
                <div>
                    {this.renderUploadConfigModal()}
                    <Button id="upload-config" type="primary" className="copy-button-font" size="small" minWidth="216px" content="UPLOAD CONFIG"
                        onClick={() => 
                        {
                            this.setState({ uploadConfigConfirmation: true });
                        }} />
                </div>);
        }
        else if (type === 're-upload-config') 
        {
            return (
                <div>
                    {this.renderUploadConfigModal()}
                    <Button id="re-upload-config" type="primary" className="re-upload-button-font" size="small" minWidth="216px" content="Re-Upload Config"
                        onClick={() => 
                        {   
                            this.csrContent=this.props.gateway!.gatewayConfig!;
                            this.setState({ uploadConfigConfirmation: true, gatewayConfig: this.props.gateway!.gatewayConfig });
                        }} />
                </div>);
        }
    }


    renderDownloadModal = () =>
    {
        return (
            <Modal id='download-modal' closeIcon={true} onClose={() => this.setState({ downloadConfirmation: false })}
                size='small'
                open={this.state.downloadConfirmation} closeOnDimmerClick={false}>
                <Modal.Header>
                    {
                        this.state.isActionFailed 
                            ? 'Unable to download Identity' 
                            : 'Download Identity'
                    }
                </Modal.Header>
                {
                    this.state.actionInProgress 
                        ?
                            <div className="content-loading upload-content-loading"><Progress circular={true} /></div> 
                        :
                            <div>
                                <Modal.Content>
                                    {
                                        this.state.isActionFailed 
                                            ? 'Download failed. Please try again.' 
                                            : 'Package ready to download'
                                    }
                                    <br /><br />
                                </Modal.Content>
                                <Modal.Footer>
                                    {
                                        this.state.isActionFailed 
                                            ?
                                                <Button id="modal-ok-btn" type="primary" size="small" content="OK" className="download-secondary-button"
                                                    onClick={() => this.setState({ downloadConfirmation: false })} /> 
                                            :
                                                <div>
                                                    <Button id="modal-close-btn" type="secondary" size="small" content="CLOSE" className="download-secondary-button"
                                                        onClick={() => this.setState({ downloadConfirmation: false })} />
                                                    <Button id="modal-download-btn" type="primary" size="small" content="DOWNLOAD" className="download-primary-button"
                                                        onClick={() => { this.downloadPackage(); }} />
                                                </div>
                                    }
                                </Modal.Footer>
                            </div>
                }
            </Modal>
        );
    }
    /**
     * Invoke the API to fetch Identity and store it in state
     */
    fetchPackageToDownload = async () =>
    {
        this.setState({ actionInProgress: true, isActionFailed: false });

        await this.fetchIdentityContent()
            .then((identityContent: any) =>
            {
                this.setState({ identity: identityContent, actionInProgress: false });
            })
            .catch((e) => 
            { 
                this.setState({ isActionFailed: true, actionInProgress: false }); 
            });
    }
    /**
     * Download "this.state.identity" content to a file 'identity.txt'
     */
    downloadPackage = () =>
    {
        let url = window.URL.createObjectURL(new Blob([this.state.identity!]));
        let ref = this.state.ref;
        if(ref.current !== null){
            ref.current!.href = url;
            ref.current!.download = `${this.props.serialNumber}.txt`;
            ref.current?.click();
        }       
    }


    renderCopyModal = () =>
    {
        return (
            <Modal closeIcon={true} onClose={() => this.setState({ copyConfirmation: false })}
                size='small'
                open={this.state.copyConfirmation} closeOnDimmerClick={false}>
                <Modal.Header>
                    {this.state.actionInProgress ? 'Fetching Identity' : (this.state.isActionFailed ? 'Unable to Copy Identity' : 'Identity Successfully Copied')}
                </Modal.Header>
                {this.state.actionInProgress ?
                    <div className="content-loading upload-content-loading"><Progress circular={true} /></div> :
                    <div>
                        <Modal.Content>
                            {this.state.isActionFailed ? 'Copy to clipboard failed. Please try again.' : 'Identity successfully copied to clipboard.'}
                            <br /><br />
                        </Modal.Content>
                        <Modal.Footer>
                            <Button id="modal-copy-btn" type="primary" size="small" content="OK" className="download-primary-button"
                                onClick={() => this.setState({ copyConfirmation: false })} />
                        </Modal.Footer>
                    </div>
                }
            </Modal>
        );
    }
    /**
     * Copy Identity to clipboard
     */
    fetchAndCopyIdentity = () =>
    {
        this.setState({ isActionFailed: false, actionInProgress: true });
        let copyContent = '';

        this.fetchIdentityContent()
            .then((identityContent: any) =>
            {
                copyContent = identityContent;
                navigator.clipboard.writeText(copyContent);

                this.setState({ identity: copyContent, actionInProgress: false });
            })
            .catch((e) => 
            { 
                navigator.clipboard.writeText('');
                this.setState({ identity: '', actionInProgress: false, isActionFailed: true }); 
            });
    }
    /**
     * 1. Fetch Identity from ForgeConnect API
     * 2. Fetch Bootstrap Certificate from SimplyConnect API
     */
    fetchIdentityContent = async (): Promise<any> => 
    {
        let gatewayType: string = this.props.gatewayType!;
        let identityContent: any = '';
        let gatewayIdentityStore = this.props.gatewayIdentityStore;

        await gatewayIdentityStore!.fetchIdentityPackage(this.props.serialNumber, this.props.organizationId, this.props.gateway!)
            .then(async (response: AxiosResponse) =>
            {
                if (response.status === 200)
                    identityContent = this.wrapContent(response.data, 65);
                else
                    this.setState({ isActionFailed: true });

                // append SimplyConnect identity to existing identityContent
                if(gatewayType && gatewayType === 'VIRTUAL' && featureFlagStore.getSimplyConnectEnabled && this.props.gateway!.providerName!==DIYAppConstants.PROVIDER_NAME_AZURE)
                {
                    await this.props.gatewayIdentityStore!.getSimplyConnectClient(this.props.serialNumber, this.props.organizationId)
                        .then((res: AxiosResponse) =>
                        {
                            if(res && res.status === 200 && res.data 
                                   && res.data.object && res.data.object.length > 0)
                            {
                                identityContent += '\n' + res.data.object[0].bootstrapCertificate;
                            }
                            else
                            {
                                identityContent = '';   // clear identityContent so that Half cert is not copied to clipboard
                                this.setState({ isActionFailed: true });
                            }
                        })
                        .catch((e) =>
                        {
                            identityContent = '';
                            this.setState({ isActionFailed: true }); 
                        });
                }
            })
            .catch((e) => 
            { 
                identityContent = '';
                this.setState({ isActionFailed: true }); 
            });

        return Promise.resolve(identityContent);
    }
    wrapContent(data: string, maxWidth: number): string
    {
        let line = '';
        let retval = '';

        for(let i=0; i<data.length; i++)
        {
            let char = data.charAt(i);
            let wrap = false;

            if(char === '\n')
            {
                wrap = true;    // line break spotted in [data] before reaching [maxWidth]
            }
            else
            {
                line += char;
                wrap = line.length === maxWidth - 1;    // 64 characters + 1 line break char
            }

            if(wrap)
            {
                retval += line + '\n';
                line = '';
            }
        }

        if(line.length > 0)
            retval += line;

        return retval;
    }


    renderUploadModal = () => 
    {
        return (
            <Modal closeIcon={true} onClose={() => this.closeUploadModal()}
                size={this.state.isActionFailed || this.state.actionInProgress ? 'small' : 'large'}
                open={this.state.uploadConfirmation}
                closeOnDimmerClick={false}
                className="upload-modal">
                <Modal.Header>
                    {
                        this.state.actionInProgress
                            ? 'Uploading Certificate Signing Request'
                            : this.state.isActionFailed
                                ? 'CSR Upload Failed'
                                : 'Upload CSR to generate Identity'
                    }
                </Modal.Header>
                <Modal.Content className="upload-csr-content">
                    {
                        this.state.actionInProgress
                            ? 
                                <div className="content-loading upload-content-loading"><Progress circular={true} /></div>
                            : 
                                this.state.isActionFailed
                                    ? 
                                        'Please try again.'
                                    : 
                                        <textarea 
                                            id="upload-textarea"
                                            defaultValue={this.csrContent}
                                            className="upload-csr-content"
                                            placeholder="Paste Certificate Signing Request here..."
                                            onChange={(e) => { this.csrContent = e.currentTarget.value }} />
                    }
                </Modal.Content>
                <Modal.Footer className="upload-modal-footer">
                    {
                        this.state.actionInProgress
                            ? ''
                            : this.state.isActionFailed
                                ?
                                <div>
                                    <Button id="modal-close-btn" type="secondary" size="small" content="CANCEL" className="upload-secondary-button"
                                        onClick={() => { this.closeUploadModal(); }} />
                                    <Button id="modal-retry-btn" type="primary" size="small" content="RETRY" className="upload-primary-button"
                                        onClick={() => { this.retryUploadButtonAction(); }} />
                                </div>
                                :
                                <div>
                                    <Button id="modal-close-btn" type="secondary" size="small" content="CANCEL" className="upload-secondary-button"
                                        onClick={() => { this.closeUploadModal(); }} />
                                    <Button id="modal-upload-btn" type="primary" size="small" content="UPLOAD" className="upload-primary-button"
                                        onClick={() => { this.uploadButtonAction(); }} />
                                </div>
                    }
                </Modal.Footer>
            </Modal>
        );
    }
    uploadButtonAction()
    {
        this.setState({ actionInProgress: true, isActionFailed: false });

        this.props.gatewayIdentityStore!.uploadCsr(this.csrContent, this.props.serialNumber, this.props.organizationId)
            .then((res: AxiosResponse) => 
            {
                if (res.status == 200 || res.status == 201)
                {
                    this.props.setGatewayCsr(this.props.serialNumber, true,'pelion','');
                    this.closeUploadModal();
                }
                else
                    this.setState({ isActionFailed: true });
                
                this.setState({ actionInProgress: false });
            })
            .catch((e) => 
            {
                this.setState({ actionInProgress: false, isActionFailed: true });
            });
    }
    /**
     * Show UPLOAD page 
     *  - retain the Pasted CSR content (in <textarea>)
     */
    retryUploadButtonAction()
    {
        this.setState({ isActionFailed: false });
        this.setState({ actionInProgress: false });
        if(this.props.gateway?.providerName===DIYAppConstants.PROVIDER_NAME_AZURE){
            this.csrContent = this.state.gatewayConfig;
        }
    }
    /**
     * Close Upload modal
     *  - clear the Pasted CSR content (from <textarea>)
     */
    closeUploadModal()
    {
        this.csrContent = '';
        this.setState({ isActionFailed: false ,errorMessage:undefined });
        this.setState({ actionInProgress: false });
        this.setState({ uploadConfirmation: false })
        this.setState({ uploadConfigConfirmation: false })
    }


    renderReUploadModal = () =>
    {
        return (
            <Modal closeIcon={true} onClose={() => this.setState({ reUploadConfirmation: false })}
                size='small'
                open={this.state.reUploadConfirmation}
                closeOnDimmerClick={false} 
                className="re-upload-modal">
                <div className="re-upload-modal-content">
                    <Modal.Header>
                        Confirm Certificate Service Request Re-upload
                    </Modal.Header>
                    <Modal.Content>
                        You are about to upload a CSR for a gateway that has already been issued an identity. 
                        <br/>
                        This should not be done for gateways that are already operational, as it will prevent remote access and application operations until a new identity is installed. 
                        <br/> <br/>
                        To install the new identity, you will need direct (non-remote) access to the gateway.
                        <br/> <br/>
                        Are you sure you wish to re-upload the CSR?
                    </Modal.Content>
                    <Modal.Footer>
                        <div>
                            <Button id="modal-close-btn" type="secondary" size="small" content="CANCEL" className="upload-secondary-button"
                                onClick={() => this.setState({ reUploadConfirmation: false })} />
                            <Button id="modal-reupload-btn" type="primary" size="small" content="REUPLOAD" className="upload-primary-button"
                                onClick={() => 
                                {
                                    this.setState({ uploadConfirmation: true });
                                    this.setState({ reUploadConfirmation: false });
                                }} />
                        </div>
                    </Modal.Footer>
                </div>
            </Modal>
        );
    }



    renderUploadConfigModal = () => 
    {
        return (
            <Modal closeIcon={true} onClose={() => this.closeUploadModal()}
                size={this.state.isActionFailed || this.state.actionInProgress ? 'small' : 'large'}
                open={this.state.uploadConfigConfirmation}
                closeOnDimmerClick={false}
                className="upload-modal">
                <Modal.Header>
                    {
                        this.state.actionInProgress
                            ? 'Uploading Gateway Configuration'
                            : this.state.isActionFailed
                                ? 'Configuration Upload Failed'
                                : 'Upload Configuration to generate Identity'
                    }
                </Modal.Header>
                <Modal.Content className="upload-csr-content">
                    {
                        this.state.actionInProgress
                            ? 
                                <div className="content-loading upload-content-loading"><Progress circular={true} /></div>
                            : 
                                this.state.isActionFailed
                                    ? 
                                        this.state.errorMessage!==undefined? this.state.errorMessage: 'Please try again.'
                                    : 
                                        <textarea 
                                            id="upload-textarea"
                                            defaultValue={this.state.gatewayConfig}
                                            className="upload-csr-content"
                                            placeholder="Paste Gateway Config here..."
                                            onChange={(e) => { this.csrContent = e.currentTarget.value }} />
                    }
                </Modal.Content>
                <Modal.Footer className="upload-modal-footer">
                    {
                        this.state.actionInProgress
                            ? ''
                            : this.state.isActionFailed
                                ?
                                <div>
                                    <Button id="modal-close-btn" type="secondary" size="small" content="CANCEL" className="upload-secondary-button"
                                        onClick={() => { this.closeUploadModal(); }} />
                                    <Button id="modal-retry-btn" type="primary" size="small" content="RETRY" className="upload-primary-button"
                                        onClick={() => { this.retryUploadButtonAction(); }} />
                                </div>
                                :
                                <div>
                                    <Button id="modal-close-btn" type="secondary" size="small" content="CANCEL" className="upload-secondary-button"
                                        onClick={() => { this.closeUploadModal(); }} />
                                    <Button id="modal-upload-btn" type="primary" size="small" content="UPLOAD" className="upload-primary-button"
                                        onClick={() => { this.uploadConfigBtnAction(); }} />
                                </div>
                    }
                </Modal.Footer>
            </Modal>
        );
    }


    uploadConfigBtnAction()
    {
        this.setState({ actionInProgress: true, isActionFailed: false });

        this.props.gatewayIdentityStore!.uploadConfig(this.csrContent, this.props.gateway!.gatewayId, this.props.organizationId,this.props.gateway!.customerId)
            .then((res: AxiosResponse) => 
            {
                if (res.status == 200 || res.status == 201)
                {
                    let serialNumber=res.data['serialNumber'];
                   
                    this.props.setGatewayCsr(this.props.gateway?.gatewayId, true,DIYAppConstants.PROVIDER_NAME_AZURE,this.csrContent);
                    this.props.setGatewaySerialNumber(this.props.gateway?.gatewayId,serialNumber);
                    this.closeUploadModal();
                }
                else
                    this.setState({ isActionFailed: true });
                
                this.setState({ actionInProgress: false });
            })
            .catch((e) => 
            {
                if (e.response.status == 422 && e.response.data.message !== undefined)
                {
                    let errorTxt:string=GlobalStrings.getErrorMessage(e.response.data.message);
                    this.setState({ actionInProgress: false, isActionFailed: true , errorMessage: errorTxt });
                }
                else {
                   this.setState({ actionInProgress: false, isActionFailed: true });
                }
            });
    }


    public static readonly DOWNLOAD_PACKAGE = "DOWNLOAD IDENTITY";
}


export class SimplyConnectGetClientResponse
{
    deviceId!: string;
    status!: string;
    systemId!: string;
    bootstrapCertificate!: string;
}